/**
 * AppChoicePage - Choose between FMA and AMS after login
 * Shows after successful authentication
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/global/auth/AuthContext";
import { redirectToOtherAppWithToken } from "@/global/auth/ssoUtils";
import {
  Building2,
  Users,
  ArrowRight,
  Sparkles,
  Settings,
  CreditCard,
  Key,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAccessCheck } from "@/hooks/useAccessCheck";
import UpgradePromptForm from "@/global/components/forms/UpgradePromptForm";
import { ShootingStars } from "@/components/ui/shooting-stars";
import { StarsBackground } from "@/components/ui/stars-background";
import { CardContainer, CardBody, CardItem } from "@/components/ui/3d-card";
import { LogOutButton } from "@/global/components/auth/LogOutButton";

export default function AppChoicePage() {
  const navigate = useNavigate();
  const { user, activeTenantId } = useAuth();

  // Check AMS access
  const amsAccess = useAccessCheck({
    app: "AMS",
    autoCheck: true,
  });

  // Redirect to login if not authenticated (use useEffect to avoid render-time navigation)
  useEffect(() => {
    if (!user || !activeTenantId) {
      navigate("/login");
    }
  }, [user, activeTenantId, navigate]);

  // Add page class for background override
  useEffect(() => {
    document.body.classList.add("app-choice-page");
    return () => {
      document.body.classList.remove("app-choice-page");
    };
  }, []);

  // Show loading or nothing while redirecting
  if (!user || !activeTenantId) {
    return null;
  }

  // Get user's first name for personalized greeting
  const firstName = user.user_metadata?.first_name || user.email?.split("@")[0] || "User";

  const handleFMAClick = async () => {
    // FMA has universal basic access for all users
    try {
      await redirectToOtherAppWithToken("fma", undefined, "/");
    } catch (error) {
      console.error("Failed to redirect to FMA:", error);
      navigate("/login");
    }
  };

  const handleAMSClick = async () => {
    // Check if user has AMS access
    if (amsAccess.hasAccess) {
      try {
        await redirectToOtherAppWithToken("ams", undefined, "/dashboard");
      } catch (error) {
        console.error("Failed to redirect to AMS:", error);
        navigate("/login");
      }
    } else {
      // Show upgrade prompt
      amsAccess.openUpgradePrompt();
    }
  };

  return (
    <>
      {/* CSS override for layout background - inline styles for immediate effect */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          /* Override body background for app choice page */
          body {
            background: transparent !important;
          }
          
          .app-choice-page .min-h-screen.bg-background {
            background: transparent !important;
          }
        `,
        }}
      />

      {/* Animated background with shooting stars */}
      <div className="fixed inset-0 bg-neutral-900 z-0"></div>
      <StarsBackground
        starDensity={0.0003}
        className="fixed inset-0 z-10"
      />
      <ShootingStars
        minSpeed={5}
        maxSpeed={30}
        minDelay={800}
        maxDelay={3000}
        starColor="#ff6b35"
        trailColor="#9E00FF"
        starWidth={15}
        starHeight={2}
        className="fixed inset-0 z-20"
      />

      <div className="min-h-screen flex items-center justify-center w-full max-w-none px-2 sm:px-4 md:px-6 py-4 sm:py-6 relative z-30">
        {/* Logout Button - Top Right */}
        <div className="fixed top-4 right-4 z-50">
          <LogOutButton 
            variant="outline" 
            size="default"
            className="bg-white/5 backdrop-blur-sm border-white/20 text-white hover:bg-white/10 hover:text-white transition-all duration-200"
          />
        </div>

        <div className="relative w-full max-w-6xl z-10">
          {/* Content - removed backdrop blur to show stars */}
          <div className="relative rounded-lg sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10">
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
                <span className="text-white">Welcome Back,</span>{" "}
                <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                  {firstName}
                </span>
              </h1>
              <p className="mt-2 text-base sm:text-lg text-gray-300 max-w-2xl mx-auto px-4 sm:px-0">
                Choose an app or manage your Orca account. FMA = Find My Agency,
                AMS = Agency Management Software.
              </p>
            </div>

            {/* Main Apps Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* FMA Card - Flagship App */}
              <CardContainer className="inter-var py-0">
                <CardBody className="bg-gradient-to-br from-[#1a2a44]/80 via-[#1a2a44]/60 to-[#1a2a44]/40 relative group/card hover:shadow-2xl hover:shadow-orange-500/[0.2] border-[#1a2a44]/40 w-full min-w-[320px] sm:min-w-[450px] h-auto rounded-xl p-6 border shadow-xl shadow-[#1a2a44]/20">
                  {/* Flagship Badge - Top Right */}
                  <CardItem
                    translateZ="80"
                    as="span"
                    className="absolute top-4 right-4 text-xs bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1 rounded-full font-medium"
                  >
                    Flagship
                  </CardItem>
                  
                  <CardItem
                    translateZ="50"
                    className="inline-flex items-center justify-center h-16 w-16 bg-gradient-to-br from-orange-400 to-orange-600 text-white rounded-xl mb-3 shadow-lg"
                  >
                    <Users className="h-8 w-8" />
                  </CardItem>
                  <CardItem
                    translateZ="60"
                    className="text-2xl font-bold text-white"
                  >
                    Find My Agency
                  </CardItem>
                  <CardItem
                    translateZ="50"
                    as="p"
                    className="text-sm text-gray-300 mt-2"
                  >
                    The premier marketplace connecting agents & agencies
                  </CardItem>
                  <CardItem translateZ="40" className="mt-4">
                    <ul className="space-y-2 text-sm text-gray-300">
                      <li>• Search agent profiles nationwide</li>
                      <li>• Post agency opportunities</li>
                      <li>• Connect with top talent</li>
                    </ul>
                  </CardItem>
                  <div className="flex justify-center mt-6">
                    <CardItem translateZ="100" className="w-full max-w-xs">
                      <Button
                        onClick={handleFMAClick}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium"
                        size="lg"
                      >
                        Launch Find My Agency{" "}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </CardItem>
                  </div>
                </CardBody>
              </CardContainer>

              {/* AMS Card */}
              <CardContainer className="inter-var py-0">
                <CardBody className="bg-gradient-to-br from-[#1a2a44]/80 via-[#1a2a44]/60 to-[#1a2a44]/40 relative group/card hover:shadow-2xl hover:shadow-orange-500/[0.2] border-[#1a2a44]/40 w-full min-w-[320px] sm:min-w-[450px] h-auto rounded-xl p-6 border shadow-xl shadow-[#1a2a44]/20">
                  {/* Premium Badge - Top Right */}
                  <CardItem
                    translateZ="80"
                    as="span"
                    className="absolute top-4 right-4 text-xs bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1 rounded-full font-medium"
                  >
                    Premium
                  </CardItem>
                  
                  <CardItem
                    translateZ="50"
                    className="inline-flex items-center justify-center h-16 w-16 bg-gradient-to-br from-orange-400 to-orange-600 text-white rounded-xl mb-3 shadow-lg"
                  >
                    <Building2 className="h-8 w-8" />
                  </CardItem>
                  <CardItem
                    translateZ="60"
                    className="text-2xl font-bold text-white"
                  >
                    Agency Management
                  </CardItem>
                  <CardItem
                    translateZ="50"
                    as="p"
                    className="text-sm text-gray-300 mt-2"
                  >
                    Powerful CRM for your agency
                  </CardItem>
                  <CardItem translateZ="40" className="mt-4">
                    <ul className="space-y-2 text-sm text-gray-300">
                      <li>• Client & agent management</li>
                      <li>• Pipeline automation</li>
                      <li>• Analytics & reporting</li>
                    </ul>
                  </CardItem>
                  <div className="flex justify-center mt-6">
                    <CardItem translateZ="100" className="w-full max-w-xs">
                      <Button
                        onClick={handleAMSClick}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium"
                        size="lg"
                      >
                        Open AMS <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </CardItem>
                  </div>
                </CardBody>
              </CardContainer>
            </div>

            {/* Account Management Section */}
            <div className="max-w-md mx-auto">
              <Card className="group transform transition-all duration-300 hover:scale-105 bg-white/3 backdrop-blur-sm border border-white/5">
                <CardHeader>
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-xl mb-3 group-hover:scale-110 transition-transform relative">
                    <img
                      src="/logos/Orca-logo-orange-white.png"
                      alt="Orca"
                      className="h-12 w-auto object-contain drop-shadow-glow"
                    />
                    <div className="absolute inset-0 bg-orange-400 blur-lg opacity-30"></div>
                  </div>
                  <CardTitle className="text-2xl text-white">
                    Manage Orca Account
                  </CardTitle>
                  <CardDescription className="text-sm text-gray-300">
                    Change plan, update email, or reset password
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li>• Billing & subscription settings</li>
                    <li>• Email & password management</li>
                    <li>• Account security & 2FA</li>
                  </ul>
                  <Button
                    onClick={() => navigate("/orca-account")}
                    className="w-full"
                    variant="secondary"
                    size="lg"
                  >
                    Open Orca Settings
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 text-center text-sm text-muted-foreground">
              <p>
                Need help?{" "}
                <a
                  href="mailto:support@findmyagency.us"
                  className="text-primary hover:underline"
                >
                  Contact Orca support
                </a>
              </p>
            </div>
          </div>

          {/* Upgrade Prompt Modal */}
          {amsAccess.requiredPlan && (
            <UpgradePromptForm
              isOpen={amsAccess.showUpgradePrompt}
              onClose={amsAccess.closeUpgradePrompt}
              currentPlan={amsAccess.currentPlan || "Free"}
              requiredPlan={amsAccess.requiredPlan}
              appName="AMS"
            />
          )}
        </div>
      </div>
    </>
  );
}
